"use client";

import { useAutoAnimate } from "@formkit/auto-animate/react";
import { useLayoutEffect, useRef } from "react";
import { useNotebookContext } from "../../../hooks/useNotebook";
import { RecentHighlightEntry } from "./recent-highlight-entry";
import { HighlightMaterials } from "./highlights";

export function RecentHighlights() {
  const { highlights, currentPage, navigatePage } = useNotebookContext();
  const [parentRef] = useAutoAnimate<HTMLDivElement>({
    duration: 180,
    easing: "ease-out",
  });

  const selectedHighlightCategory =
    currentPage.type === "highlight_category"
      ? currentPage.value
      : currentPage.type === "source_highlight_category"
      ? currentPage.highlight_category
      : null;

  const selectedHighlight =
    currentPage.type === "highlight"
      ? currentPage.value
      : currentPage.type === "source_highlight"
      ? currentPage.highlight
      : null;

  const selectHighlight = (highlight: HighlightMaterials) =>
    navigatePage({ type: "highlight", value: highlight });

  // Stores the previous render's ids
  const prevIdsRef = useRef<Set<number>>(new Set());

  const MAX_VISIBLE = 5;

  const sortedHighlights = [...highlights]
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )
    .slice(0, MAX_VISIBLE);

  const currentIds = new Set(sortedHighlights.map((h) => h.id));

  // Update snapshot AFTER render (Strict Mode safe)
  useLayoutEffect(() => {
    prevIdsRef.current = currentIds;
  }, [highlights]);

  if (!highlights?.length) return null;

  // Detect the truly new highlight (render-phase safe)
  const newestHighlightId =
    sortedHighlights.find((h) => !prevIdsRef.current.has(h.id))?.id ?? null;

  return (
    <>
      {/* Component-local animation CSS */}
      <style>{`
        @keyframes highlight-expand {
            0% {
                opacity: 0;
                transform: scaleY(0.6);
            }
            55% {
                opacity: 1;
                transform: scaleY(1.04);
            }
            100% {
                transform: scaleY(1);
            }
        }

       @keyframes highlight-glow {
            0% {
                filter:
                /*drop-shadow(0 0 0 rgba(16, 185, 129, 0))*/
                brightness(1);
            }
            45% {
                filter:
                /*drop-shadow(0 0 6px rgba(16, 185, 129, 0.45))*/
                brightness(1.08);
            }
            100% {
                filter:
                /*drop-shadow(0 0 0 rgba(16, 185, 129, 0))*/
                brightness(1);
            }
        }

        .animate-highlight-expand {
          transform-origin: top;
          animation: highlight-expand 1000ms ease, highlight-glow 1000ms ease;
        }
      `}</style>

      <div
        ref={parentRef}
        className="px-2 pb-4 pt-2 space-y-2 group-data-[collapsible=icon]:px-0"
      >
        {sortedHighlights.map((highlight, index) => {
          const isNewest = highlight.id === newestHighlightId && index === 0;
          const isSelected = selectedHighlight?.id == highlight.id;

          return (
            <div
              key={highlight.id}
              className={
                isNewest
                  ? "animate-highlight-expand animate-highlight-glow"
                  : undefined
              }
            >
              <RecentHighlightEntry
                highlight={highlight}
                isSelected={isSelected}
                onSelect={() => selectHighlight(highlight)}
                className={
                  !isSelected && selectedHighlightCategory
                    ? "opacity-40 hover:opacity-80"
                    : ""
                }
              />
            </div>
          );
        })}
      </div>
    </>
  );
}
